import { ICalendar } from 'pages/my-calendars/add-calendar'
import { STATUS_CONFIG } from 'pages/my-calendars/constants'
import type { FC } from 'react'
import { useContext, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, Popover } from 'antd'
import { DeleteOutlined, EyeOutlined, FormOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { userInfoSelector } from 'redux/selectors'
import { SupplierCalendarsContext } from 'contexts/SupplierCalendarsContext'
import { checkSuperAdminRole } from 'shared/utils'

export const getColumns = (
    onDeleteButtonClick: (calendarId: string, calendarName: string) => void
) => [
    {
        title: 'Дата создания',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: '125px',
        render: (date: string) => (
            <span className="cell">{new Date(date).toLocaleDateString('ru-Ru')}</span>
        )
    },
    {
        title: 'Название календаря',
        dataIndex: 'name',
        key: 'name',
        width: '200px',
        render: (text: string) => <span className="cell">{text}</span>
    },
    {
        title: 'Статус календаря',
        dataIndex: 'status',
        key: 'status',
        width: '200px',
        render: (status: string, { id, name }: ICalendar) => (
            <span className="status-content cell">
                {STATUS_CONFIG[status]}
                <StatusActions
                    status={status}
                    calendarId={id}
                    calendarName={name}
                    onDeleteButtonClick={onDeleteButtonClick}
                />
            </span>
        )
    }
]

const StatusActions: FC<{
    status: string
    calendarId: string
    calendarName: string
    onDeleteButtonClick: (calendarId: string, calendarName: string) => void
}> = ({ status, calendarId, calendarName, onDeleteButtonClick }) => {
    const history = useHistory()
    const userInfo = useSelector(userInfoSelector)
    const { isAdmin } = userInfo

    const isSuperAdmin = checkSuperAdminRole()

    const { setCalendarId } = useContext(SupplierCalendarsContext)

    const isDisabledOpenButton = status === 'Standard'

    // отключаем возможность редактирования утвержденных календарей
    /*const isDisabledEditButton =
        ((status === 'Approved' || isAdmin) && !isSuperAdmin) || status === 'Standard'*/
    const isDisabledEditButton = status === 'Approved' || isAdmin || status === 'Standard'

    const isDisabledDeleteButton =
        (status === 'Approved' || status === 'Standard' || isAdmin) && !isSuperAdmin

    const deleteContent = useMemo(
        () => <span>Календарь и все внесенные изменения будут удалены</span>,
        []
    )

    const openContent = useMemo(() => <span>Открыть календарь для просмотра</span>, [])

    const goTo = (url: string) => {
        history.push(url)
    }

    const editContent = useMemo(() => <span>Открыть календарь для редактирования</span>, [])

    const handleOpenCalendar = () => {
        goTo(`/my-calendars/info/${calendarId}`)
        setCalendarId(calendarId)
    }

    const handleEditCalendar = () => {
        goTo(`/my-calendars/edit/${calendarId}`)
        setCalendarId(calendarId)
    }

    return (
        <div className="container">
            <Popover
                content={openContent}
                title="Открыть календарь"
                overlayClassName="calendars-action-popover"
            >
                <Button
                    type="text"
                    className={`icon-button ${isDisabledOpenButton ? 'hover-disabled' : ''}`}
                    disabled={isDisabledOpenButton}
                    onClick={handleOpenCalendar}
                >
                    <EyeOutlined className="icon" />
                </Button>
            </Popover>
            <Popover
                content={editContent}
                title="Редактировать календарь"
                overlayClassName="calendars-action-popover"
            >
                <Button
                    type="text"
                    className={`icon-button ${isDisabledEditButton ? 'hover-disabled' : ''}`}
                    disabled={isDisabledEditButton}
                    onClick={handleEditCalendar}
                >
                    <FormOutlined className="icon" />
                </Button>
            </Popover>
            <Popover
                content={deleteContent}
                title="Удалить календарь"
                overlayClassName="calendars-action-popover"
            >
                <Button
                    type="text"
                    className={`icon-button ${isDisabledDeleteButton ? 'hover-disabled' : ''}`}
                    disabled={isDisabledDeleteButton}
                    onClick={() => onDeleteButtonClick(calendarId, calendarName)}
                >
                    <DeleteOutlined className="icon__no-margin" />
                </Button>
            </Popover>
        </div>
    )
}
